import React, { Component } from "react";
import { Card, Button, Row, Col, Modal } from "antd";
import debounce from "lodash.debounce";
import Filters, { FilterItem, Types } from "../../components/Filters";
import { ShelterConsumer, ShelterProvider } from "../../context/ShelterContext";
import { PartnerKeyTypeArray } from "../../interfaces/IIntegration";
import IntegrationCredentialsList from "../../components/IntegrationCredentialsList";
import RegisterCredentialForm from "../../components/Forms/Credentials";
import { setToken, integrationCredentialList } from "../../services/integration";
import { loadStorage } from "../../services/storage";
import { STORAGE_KEY } from "../../context/AuthContext";

export default class IntegrationCredentials extends Component {
  state = {
    openedModal: false,
    selectedItem: null as any,
    fetching: false,
    credentials: [] as any[],
    pagination: null as any,
  };

  private listRef = React.createRef<IntegrationCredentialsList>();

  private credentialFilterDispatchOptions = {
    search: {},
    options: {},
  } as const;

  credentialListSearch = Object.assign(
    {},
    this.credentialFilterDispatchOptions
  );

  constructor(props: any) {
    super(props);

    // Setup admin session against the integration API here (in the constructor,
    // which runs top-down) instead of componentDidMount (which runs bottom-up).
    // The child list fires its first request from its own componentDidMount,
    // which would otherwise run before this parent's — sending it tokenless.
    const store = loadStorage(STORAGE_KEY);
    if (store) {
      setToken(store.accessToken);
    }
  }

  handleFiltersSubmit = (payload: any) => {
    if (payload && payload.search) {
      this.credentialListSearch = {
        ...this.credentialListSearch,
        search: payload.search,
      };
    }
  };

  loadCredentials = async (payload: any = {}) => {
    this.setState({ fetching: true });
    try {
      const response = await integrationCredentialList({
        ...payload,
        search: {
          ...payload.search,
          shelter: {
            with: true,
          },
        },
      });
      const data = response?.data?.data;

      this.setState({
        credentials: data?.result || [],
        pagination: data
          ? {
              count: data.count,
              limit: data.limit,
              page: data.page,
              pages: data.pages,
            }
          : null,
      });
    } finally {
      this.setState({ fetching: false });
    }
  };

  renderAdd = () => {
    const { permissions }: any = this.props;
    if (permissions && !permissions.create) return null;

    return (
      <Button
        type="primary"
        shape="round"
        onClick={() => this.setState({ openedModal: true })}
      >
        Create new credential
      </Button>
    );
  };

  handleModalClose = () => {
    this.setState({ selectedItem: null, openedModal: false });
    this.listRef?.current?.load();
  };

  editItem = (item: any) => {
    this.setState({ selectedItem: item, openedModal: true });
  };

  render() {
    const { permissions, history }: any = this.props;
    const { fetching, credentials, pagination } = this.state;

    return (
      <ShelterProvider>
        <ShelterConsumer>
          {({ shelters, fetching: shelterFetching, requestShelterList }) => {
            const loadSheltersDispatch = (args: any = {}) =>
              requestShelterList({
                search: {
                  ...args
                },
                options: {
                  limit: -1,
                  sort: ["name"],
                  _force_ids: true,
                },
              });

            const delayedSearch = debounce((v) => {
              if (v) {
                loadSheltersDispatch({ name: { ilike: `%${v}%` } });
              }
            }, 500);

            const sheltersToFilter = shelters?.map((v) => ({
              value: v.id,
              text: v.name,
            }));

            const partnerToFilter = PartnerKeyTypeArray.map((v) => ({
              value: v,
              text: v,
            }));

            return (
              <>
                <Card title="Filters" style={{ marginBottom: 25 }}>
                  <Filters
                    size="medium"
                    loading={fetching}
                    history={history}
                    defaultSubmitOptions={this.credentialFilterDispatchOptions}
                    dispatches={[loadSheltersDispatch]}
                    submitDispatch={this.loadCredentials}
                    onSubmit={this.handleFiltersSubmit}
                  >
                    {({ values, handleFilterChange }: any) => (
                      <Row gutter={9} style={{ width: "100%" }}>
                        <Col xs={24} md={9}>
                          <FilterItem
                            name="description.ilike"
                            type={Types.INPUT}
                            placeholder="Filter by description"
                            value={values["description.ilike"]}
                            size="medium"
                            options={{
                              input: {
                                allowClear: true,
                              },
                            }}
                            onChange={handleFilterChange}
                          />
                        </Col>

                        <Col xs={24} md={9}>
                          <FilterItem
                            name="shelter_id"
                            type={Types.SELECT}
                            placeholder="Shelter"
                            data={sheltersToFilter}
                            value={values["shelter_id"]}
                            size="medium"
                            loading={shelterFetching}
                            options={{
                              select: {
                                allowClear: true,
                                showSearch: true,
                                onDropdownVisibleChange: (open) => {
                                  if (open) {
                                    loadSheltersDispatch();
                                  }
                                },
                                onSearch: async (v) => {
                                  delayedSearch(v);
                                },
                              },
                            }}
                            onChange={handleFilterChange}
                          />
                        </Col>

                        <Col xs={24} md={6}>
                          <FilterItem
                            name="partner_key"
                            type={Types.SELECT}
                            placeholder="Partner"
                            data={partnerToFilter}
                            value={values["partner_key"]}
                            size="medium"
                            options={{
                              select: {
                                allowClear: true,
                                showSearch: true,
                              },
                            }}
                            onChange={handleFilterChange}
                          />
                        </Col>
                      </Row>
                    )}
                  </Filters>
                </Card>

                <Modal
                  title={
                    this.state.selectedItem
                      ? "Update Credential"
                      : "New Credential"
                  }
                  visible={this.state.openedModal}
                  footer={null}
                  destroyOnClose={true}
                  onCancel={() => this.handleModalClose()}
                >
                  <RegisterCredentialForm
                    selectedItem={this.state.selectedItem}
                    closeModal={this.handleModalClose}
                    shelters={shelters}
                    loadShelters={loadSheltersDispatch}
                    loadingShelters={shelterFetching}
                  />
                </Modal>

                <Card title="List of Credentials" extra={this.renderAdd()}>
                  <IntegrationCredentialsList
                    ref={this.listRef}
                    loading={fetching}
                    credentials={credentials}
                    pagination={pagination}
                    permissions={permissions}
                    history={history}
                    searchOptions={this.credentialListSearch}
                    loadCredentials={this.loadCredentials}
                    editItem={this.editItem}
                  />
                </Card>
              </>
            );
          }}
        </ShelterConsumer>
      </ShelterProvider>
    );
  }
}
